import React from 'react';
import { InvoiceTemplateType, invoiceTemplateOptions, InvoiceTemplateInterface } from '../utils/invoiceTemplates';
import { Box, Typography, Button, Grid, Paper, useTheme } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

interface InvoiceTemplateSelectorProps {
  selectedTemplateId: number;
  onSelectTemplate: (templateId: number) => void;
  onPreview: (templateId: number) => void;
  customTemplates?: InvoiceTemplateInterface[];
}

const InvoiceTemplateSelector: React.FC<InvoiceTemplateSelectorProps> = ({
  selectedTemplateId,
  onSelectTemplate,
  onPreview,
  customTemplates = []
}) => {
  const theme = useTheme();

  // combine standard and custom templates
  const allTemplates = [...invoiceTemplateOptions, ...customTemplates];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Select Invoice Template</Typography>
      <Grid container spacing={3}>
        {allTemplates.map((template) => {
          const isSelected = selectedTemplateId === template.id;
          return (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Paper
                elevation={isSelected ? 4 : 1}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  transition: 'all 0.2s',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => onSelectTemplate(template.id)}
              >
                {/* Visual Preview Abstraction */}
                <Box
                  sx={{
                    height: 140,
                    bgcolor: template.colorScheme?.background || '#fff',
                    border: `1px solid ${template.colorScheme?.secondary || '#eee'}`,
                    borderRadius: 1,
                    mb: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Header Stripe */}
                  <Box sx={{
                    height: template.variant === 'modern' ? 40 : 10,
                    bgcolor: template.colorScheme?.primary,
                    width: '100%'
                  }} />

                  {/* Content Abstract */}
                  <Box sx={{ p: 1, flex: 1 }}>
                    {template.variant === 'futuristic' ? (
                      <Box sx={{ bgcolor: '#000', height: '100%', width: '100%', opacity: 0.1 }} />
                    ) : (
                      <>
                        <Box sx={{ height: 8, width: '40%', bgcolor: '#eee', mb: 1, borderRadius: 0.5 }} />
                        <Box sx={{ height: 8, width: '60%', bgcolor: '#eee', mb: 2, borderRadius: 0.5 }} />
                        <Box sx={{ height: 4, width: '100%', bgcolor: '#f5f5f5', mb: 0.5 }} />
                        <Box sx={{ height: 4, width: '100%', bgcolor: '#f5f5f5', mb: 0.5 }} />
                        <Box sx={{ height: 4, width: '100%', bgcolor: '#f5f5f5', mb: 0.5 }} />
                      </>
                    )}
                  </Box>

                  {isSelected && (
                    <Box sx={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      bgcolor: 'white',
                      borderRadius: '50%',
                    }}>
                      <CheckCircleIcon color="primary" />
                    </Box>
                  )}
                </Box>

                <Typography variant="subtitle1" fontWeight="bold">
                  {template.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                  {template.description}
                </Typography>

                <Button
                  variant={isSelected ? "contained" : "outlined"}
                  fullWidth
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectTemplate(template.id);
                  }}
                >
                  {isSelected ? 'Selected' : 'Use Template'}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview(template.id);
                  }}
                >
                  Preview PDF
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default InvoiceTemplateSelector;